import { useSupabaseQuery, useSupabaseMutation, callSupabaseFunction } from './useSupabase'
import type { ReportStatus, ReportPriority } from '../types/supabase'
import type { 
  SubmitReportResponse,
  GetReportWithMessagesResponse,
  GetOrganizationReportsResponse,
  UpdateReportStatusResponse,
  GetReportAuditLogsResponse
} from '../types/api'
import type {
  SubmitReportParams,
  GetReportWithMessagesParams,
  GetOrganizationReportsParams,
  UpdateReportStatusParams,
  GetReportDetailsParams,
  GetReportAuditLogsParams,
  UpdateReportPriorityParams,
  UpdateReportDueDateParams,
  BulkUpdateReportsParams
} from '../types/supabase-functions'
import { useAuth } from '../contexts/useAuthContext'
import { useQueryClient } from '@tanstack/react-query'

// Submit anonymous report
export function useSubmitReport() {
  const queryClient = useQueryClient()
  
  return useSupabaseMutation<
    SubmitReportResponse,
    {
      organizationId: string
      content: string
      category?: string
      reporterAlias?: string
      reporterContact?: string
      encryptionKey?: string
    }
  >(
    async (variables) => {
      return callSupabaseFunction<SubmitReportParams, SubmitReportResponse>('submit_report', {
        p_organization_id: variables.organizationId,
        p_content: variables.content,
        p_category: variables.category,
        p_reporter_alias: variables.reporterAlias,
        p_reporter_contact: variables.reporterContact,
        p_encryption_key: variables.encryptionKey || 'default_encryption_key',
      });
    },
    {
      onSuccess: () => {
        // Invalidate reports list if user is logged in
        queryClient.invalidateQueries({ queryKey: ['reports'] })
      },
    }
  )
}

// Get report by case ID (anonymous access)
export function useReportByCaseId(caseId: string, encryptionKey?: string) {
  return useSupabaseQuery<GetReportWithMessagesResponse>(
    ['report', caseId],
    async () => {
      return callSupabaseFunction<GetReportWithMessagesParams, GetReportWithMessagesResponse>('get_report_with_messages', {
        p_case_id: caseId,
        p_encryption_key: encryptionKey || 'default_encryption_key',
      })
    },
    {
      enabled: !!caseId,
      staleTime: 30 * 1000, // Consider data stale after 30 seconds
    }
  )
}

// Get organization reports (authenticated)
export function useOrganizationReports(
  status?: string,
  limit: number = 50,
  offset: number = 0
) {
  const { user } = useAuth()
  
  return useSupabaseQuery<GetOrganizationReportsResponse>(
    ['reports', user?.id, status, limit, offset],
    async () => {
      if (!user?.id) {
        throw new Error('User ID is required to fetch reports');
      }
      
      const params: GetOrganizationReportsParams = {
        p_user_id: user.id,
        p_status: status,
        p_limit: limit,
        p_offset: offset,
      };
      
      // callSupabaseFunction returns { data, error }
      return callSupabaseFunction<GetOrganizationReportsParams, GetOrganizationReportsResponse>('get_organization_reports', params);
    },
    {
      enabled: !!user?.id,
      staleTime: 60 * 1000, // Consider data stale after 1 minute
      retry: 1, // Only retry once
    }
  )
}

// Update report status
export function useUpdateReportStatus() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  
  return useSupabaseMutation<
    UpdateReportStatusResponse,
    {
      reportId: string
      status: ReportStatus
      assignedTo?: string
    }
  >(
    async (variables) => {
      return callSupabaseFunction<UpdateReportStatusParams, UpdateReportStatusResponse>('update_report_status', {
        p_report_id: variables.reportId,
        p_status: variables.status,
        p_user_id: user?.id,
        p_assigned_to: variables.assignedTo,
      })
    },
    {
      onSuccess: () => {
        // Invalidate specific report and reports list
        queryClient.invalidateQueries({ queryKey: ['report'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
      },
    }
  )
}

// Assign report to user
export function useAssignReport() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  
  return useSupabaseMutation<
    UpdateReportStatusResponse,
    {
      reportId: string
      assignedToUserId: string
    }
  >(
    async (_variables) => {
      // Get current report to preserve status
      const { data: report } = await callSupabaseFunction<
        GetReportDetailsParams,
        { status: string }
      >('get_report_details', { p_report_id: _variables.reportId })
      
      const result = await callSupabaseFunction<UpdateReportStatusParams, UpdateReportStatusResponse>('update_report_status', {
        p_report_id: _variables.reportId,
        p_status: report?.status || 'pending',
        p_user_id: user?.id,
        p_assigned_to: _variables.assignedToUserId,
      })
      return { data: result.data as UpdateReportStatusResponse, error: result.error }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] })
      },
    }
  )
}

// Update report priority
export function useUpdateReportPriority() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  
  return useSupabaseMutation<
    {
      success: boolean
      old_priority: ReportPriority
      new_priority: ReportPriority
      error?: string
    },
    {
      reportId: string
      priority: ReportPriority
    }
  >(
    async (variables) => {
      if (!user) throw new Error('User not authenticated')
      
      const result = await callSupabaseFunction<
        UpdateReportPriorityParams,
        {
          success: boolean
          old_priority: ReportPriority
          new_priority: ReportPriority
          error?: string
        }
      >('update_report_priority', {
        p_report_id: variables.reportId,
        p_priority: variables.priority,
        p_user_id: user.id
      })
      
      if (!result.data?.success) {
        throw new Error(result.data?.error || 'Failed to update priority')
      }
      
      return { data: result.data, error: result.error }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['report'] })
        queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
      }
    }
  )
}

// Update report due date
export function useUpdateReportDueDate() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  
  return useSupabaseMutation<
    {
      success: boolean
      old_due_date: string | null
      new_due_date: string | null
      error?: string
    },
    {
      reportId: string
      dueDate: string | null
    }
  >(
    async (variables) => {
      if (!user) throw new Error('User not authenticated')
      
      const result = await callSupabaseFunction<
        UpdateReportDueDateParams,
        {
          success: boolean
          old_due_date: string | null
          new_due_date: string | null
          error?: string
        }
      >('update_report_due_date', {
        p_report_id: variables.reportId,
        p_due_date: variables.dueDate,
        p_user_id: user.id
      })
      
      if (!result.data?.success) {
        throw new Error(result.data?.error || 'Failed to update due date')
      }
      
      return { data: result.data, error: result.error }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['report'] })
        queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
      }
    }
  )
}

// Bulk update reports
export function useBulkUpdateReports() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  
  return useSupabaseMutation<
    {
      success: boolean
      updated_count: number
      total_count: number
      error?: string
    },
    {
      reportIds: string[]
      operation: 'status' | 'priority' | 'assign'
      value: string
    }
  >(
    async (variables) => {
      if (!user) throw new Error('User not authenticated')
      
      const result = await callSupabaseFunction<
        BulkUpdateReportsParams,
        {
          success: boolean
          updated_count: number
          total_count: number
          error?: string
        }
      >('bulk_update_reports', {
        p_report_ids: variables.reportIds,
        p_operation: variables.operation,
        p_value: variables.value,
        p_user_id: user.id
      })
      
      if (!result.data?.success) {
        throw new Error(result.data?.error || 'Failed to update reports')
      }
      
      return { data: result.data, error: result.error }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['report'] })
        queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
      }
    }
  )
}

// Get report by ID (for admin detail view)
export function useReportById(reportId: string) {
  const { user } = useAuth()
  
  return useSupabaseQuery<GetReportWithMessagesResponse>(
    ['report', reportId],
    async () => {
      // First get the report to get the case_id
      const { data: reports } = await callSupabaseFunction<
        GetOrganizationReportsParams,
        GetOrganizationReportsResponse
      >('get_organization_reports', {
        p_user_id: user?.id,
        p_status: undefined,
        p_limit: 1000,
        p_offset: 0
      })
      
      // Find the report by ID
      const report = reports?.reports?.find((r) => r.id === reportId)
      if (!report) {
        throw new Error('Report not found')
      }
      
      // Now get the full report with messages using case_id
      const result = await callSupabaseFunction<GetReportWithMessagesParams, GetReportWithMessagesResponse>('get_report_with_messages', {
        p_case_id: report.case_id,
        p_encryption_key: 'default_encryption_key'
      })
      
      // Add additional fields from the reports list
      if (result.data?.report) {
        const reportWithAssignment = result.data.report as typeof result.data.report & { 
          assigned_to?: string
          assigned_to_name?: string 
        }
        reportWithAssignment.assigned_to = (report as typeof report & { assigned_to?: string }).assigned_to
        reportWithAssignment.assigned_to_name = report.assigned_to_name
      }
      
      return { data: result.data as GetReportWithMessagesResponse, error: result.error }
    },
    {
      enabled: !!user && !!reportId,
      staleTime: 30 * 1000,
    }
  )
}

// Get audit logs for a report
export function useReportAuditLogs(reportId: string) {
  const { user } = useAuth()
  
  return useSupabaseQuery<GetReportAuditLogsResponse>(
    ['auditLogs', reportId],
    async () => {
      const result = await callSupabaseFunction<GetReportAuditLogsParams, GetReportAuditLogsResponse>('get_report_audit_logs', {
        p_report_id: reportId,
        p_user_id: user?.id
      })
      return { data: result.data as GetReportAuditLogsResponse, error: result.error }
    },
    {
      enabled: !!user && !!reportId,
      staleTime: 60 * 1000, // Consider data stale after 1 minute
    }
  )
}

